import React, { useEffect, useRef, useState } from "react";
import "./CoachesAndSporters.css";
import { apiFetch } from "../../helpers/api";
import { useTranslation } from "react-i18next";
import ProfileModal from "./ProfileModal";

const Coaches = () => {
  const [coaches, setCoaches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const scrollContainerRef = useRef(null);
  const [selectedCoach, setSelectedCoach] = useState(null);
  const { t } = useTranslation();

  useEffect(() => {
    const fetchCoaches = async () => {
      try {
        const data = await apiFetch("/api/coaches");
        setCoaches(data);
      } catch (err) {
        console.error("Failed to fetch coaches:", err);
        setError("Failed to load coaches.");
      } finally {
        setLoading(false);
      }
    };

    fetchCoaches();
  }, []);

  useEffect(() => {
    const el = scrollContainerRef.current;

    const updateScrollState = () => {
      if (!el) {
        setCanScrollLeft(false);
        setCanScrollRight(false);
        return;
      }

      const maxScrollLeft = el.scrollWidth - el.clientWidth;
      const hasOverflow = maxScrollLeft > 1;

      setCanScrollLeft(hasOverflow && el.scrollLeft > 1);
      setCanScrollRight(hasOverflow && el.scrollLeft < maxScrollLeft - 1);
    };

    updateScrollState();
    el?.addEventListener("scroll", updateScrollState, { passive: true });
    window.addEventListener("resize", updateScrollState);

    return () => {
      el?.removeEventListener("scroll", updateScrollState);
      window.removeEventListener("resize", updateScrollState);
    };
  }, [coaches]);

  const scrollLeft = () => {
    scrollContainerRef.current?.scrollBy({ left: -300, behavior: "smooth" });
  };

  const scrollRight = () => {
    scrollContainerRef.current.scrollBy({ left: 300, behavior: "smooth" });
  };

  const hasProfileData = (coach) => Boolean(
    coach.profileTitle || coach.biography || coach.achievements?.length
  );

  return (
    <section className="about-sporters-coaches">
      <h3>{t("aboutUs.coaches")}</h3>
      {loading && <p>Loading...</p>}
      {error && <p className="error">{error}</p>}

      <div className="carousel-wrapper">
        {canScrollLeft && (
          <button
            className="scroll-button left"
            onClick={scrollLeft}
            aria-label="Previous coach"
          >
            &#10094;
          </button>
        )}

        <div className="carousel" ref={scrollContainerRef}>
          {coaches.map((coach) => (
            <button
              className="card profile-card"
              type="button"
              key={coach.id || coach.name}
              onClick={() => hasProfileData(coach) && setSelectedCoach(coach)}
              disabled={!hasProfileData(coach)}
            >
              <div className="card-image">
                <img src={coach.imageUrl} alt={coach.name} />
                {hasProfileData(coach) && <span className="profile-card__hover"><strong>{coach.biography || coach.profileTitle}</strong><em>Visit profile →</em></span>}
              </div>
              <div className="card-info">
                <h4>
                  {coach.name} {coach.surname}
                </h4>
                <p>{coach.title || "Coach"}</p>
              </div>
            </button>
          ))}
        </div>

        {canScrollRight && (
          <button
            className="scroll-button right"
            onClick={scrollRight}
            aria-label="Next coach"
          >
            &#10095;
          </button>
        )}
      </div>
      <ProfileModal profile={selectedCoach} type="coach" onClose={() => setSelectedCoach(null)} />
    </section>
  );
};

export default Coaches;
